import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * LanguageSwitcher - flag dropdown for the top bar.
 * Self-contained: reads/writes language via the i18n context.
 */
import { useState, useRef, useEffect } from 'react';
import { useTranslation, LANGUAGES } from './i18n.js';
/** Small inline SVG flags (never emoji — emoji render inconsistently across OS). */
const Flag = ({ lang, className }) => {
    const common = { viewBox: '0 0 20 14', className, preserveAspectRatio: 'none' };
    if (lang === 'pl') {
        return (_jsxs("svg", { ...common, children: [_jsx("rect", { width: "20", height: "7", fill: "#ffffff" }), _jsx("rect", { y: "7", width: "20", height: "7", fill: "#dc143c" })] }));
    }
    if (lang === 'es') {
        return (_jsxs("svg", { ...common, children: [_jsx("rect", { width: "20", height: "14", fill: "#c60b1e" }), _jsx("rect", { y: "3.5", width: "20", height: "7", fill: "#ffc400" })] }));
    }
    // en — St George's cross (recognisable, simple)
    return (_jsxs("svg", { ...common, children: [_jsx("rect", { width: "20", height: "14", fill: "#ffffff" }), _jsx("rect", { x: "8", width: "4", height: "14", fill: "#ce1124" }), _jsx("rect", { y: "5", width: "20", height: "4", fill: "#ce1124" })] }));
};
const ChevronDown = ({ className }) => (_jsx("svg", { className: className, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("polyline", { points: "6 9 12 15 18 9" }) }));
const Check = ({ className }) => (_jsx("svg", { className: className, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("polyline", { points: "20 6 9 17 4 12" }) }));
export const LanguageSwitcher = () => {
    const { language, setLanguage, t } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target))
                setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    return (_jsxs("div", { ref: ref, className: "relative", children: [_jsxs("button", { onClick: () => setOpen((o) => !o), className: "flex items-center gap-1.5 px-2 py-1.5 rounded-md text-muted hover:text-text hover:bg-surface2 transition-colors", title: t('common.language'), "aria-label": t('common.language'), "aria-haspopup": "listbox", "aria-expanded": open, children: [_jsx("span", { className: "w-5 h-3.5 rounded-sm overflow-hidden border border-border inline-flex", children: _jsx(Flag, { lang: language, className: "w-full h-full" }) }), _jsx("span", { className: "text-xs font-medium uppercase", children: language }), _jsx(ChevronDown, { className: "w-3 h-3" })] }), open && (_jsx("div", { role: "listbox", "aria-label": t('common.language'), className: "absolute right-0 top-full mt-1 w-44 bg-surface border border-border rounded-lg shadow-lg p-1 z-floating", children: LANGUAGES.map((lng) => {
                    const active = lng === language;
                    return (_jsxs("button", { role: "option", "aria-selected": active, onClick: () => {
                            setLanguage(lng);
                            setOpen(false);
                        }, className: `w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors ${active ? 'bg-surface2 text-text' : 'text-muted hover:text-text hover:bg-surface2'}`, children: [_jsx("span", { className: "w-5 h-3.5 rounded-sm overflow-hidden border border-border inline-flex", children: _jsx(Flag, { lang: lng, className: "w-full h-full" }) }), _jsx("span", { className: "flex-1 text-left", children: t(`languages.${lng}`) }), active && _jsx(Check, { className: "w-4 h-4 text-accent" })] }, lng));
                }) }))] }));
};
export default LanguageSwitcher;
//# sourceMappingURL=LanguageSwitcher.js.map